import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';
import { ApiError } from '@/lib/api';
import { Alert, Button, Card, Spinner } from '@/components/ui';

/**
 * Landingssiden efter Microsoft-login.
 *
 * Supabase sender brugeren hertil med en engangskode i URL'en. Koden byttes til en
 * session, som afleveres til API'et og bliver til en cookie - se AuthProvider.
 */
export function AuthCallbackPage() {
  const { completeOAuthLogin } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  /**
   * Koden kan kun indloeses én gang. React koerer effekter to gange i StrictMode under
   * udvikling, og uden denne vagt ville anden koersel fejle paa en kode der allerede er
   * brugt - et login der ser broedt ud, men kun i udvikling.
   */
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    // Microsoft og Supabase melder fejl tilbage i URL'en frem for som HTTP-status.
    const denied = params.get('error_description') ?? params.get('error');
    if (denied) {
      setError(denied);
      return;
    }

    const code = params.get('code');
    if (!code) {
      setError('Svaret fra Microsoft manglede en login-kode.');
      return;
    }

    void completeOAuthLogin(code)
      .then(() => navigate('/dashboard', { replace: true }))
      .catch((loginError: unknown) => {
        // Beskeden fra API'et er allerede dansk og brugervendt - fx naar profilen
        // mangler, eller brugeren ingen rolle har.
        setError(
          loginError instanceof ApiError
            ? loginError.message
            : loginError instanceof Error
              ? loginError.message
              : 'Login mislykkedes.',
        );
      });
  }, [completeOAuthLogin, navigate, params]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas p-6">
        <Card className="w-full max-w-sm p-6">
          <h1 className="mb-3 text-base font-semibold">Login mislykkedes</h1>
          <Alert tone="error">{error}</Alert>
          <Button className="mt-4 w-full" onClick={() => navigate('/login', { replace: true })}>
            Tilbage til login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-3">
      <Spinner className="h-6 w-6" />
      <p className="text-xs text-ink-muted">Logger dig ind…</p>
    </div>
  );
}
